import React, { useRef, useCallback, useImperativeHandle, useEffect } from 'react';

import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';

import { $getRoot, $getSelection, $isRangeSelection, FORMAT_TEXT_COMMAND, UNDO_COMMAND, REDO_COMMAND } from 'lexical';
import { $generateHtmlFromNodes, $generateNodesFromDOM } from '@lexical/html';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { ListNode, ListItemNode, INSERT_ORDERED_LIST_COMMAND, INSERT_UNORDERED_LIST_COMMAND, REMOVE_LIST_COMMAND } from '@lexical/list';
import { AutoLinkNode, LinkNode } from '@lexical/link';


import './LexicalRichTextEditor.css';

// ── Toolbar ──────────────────────────────────────────────────────────────────

const FORMATS = ['bold', 'italic', 'underline', 'strikethrough'];

function ToolbarPlugin({ onFormatState }) {
    const [editor] = useLexicalComposerContext();
    const [activeFormats, setActiveFormats] = React.useState({});
    const [listType, setListType] = React.useState(null);
    const [alignment, setAlignment] = React.useState('left');

    useEffect(() => {
        return editor.registerUpdateListener(({ editorState }) => {
            editorState.read(() => {
                const selection = $getSelection();
                if (!$isRangeSelection(selection)) return;

                const formats = {};
                FORMATS.forEach(f => { formats[f] = selection.hasFormat(f); });
                setActiveFormats(formats);

                const anchorNode = selection.anchor.getNode();
                const element = anchorNode.getKey() === 'root'
                    ? anchorNode
                    : anchorNode.getTopLevelElementOrThrow();

                if (element instanceof ListNode || element.getParent() instanceof ListNode) {
                    const listNode = element instanceof ListNode ? element : element.getParent();
                    setListType(listNode.getListType());
                } else {
                    setListType(null);
                }

                const formatType = element.getFormatType ? element.getFormatType() : 'left';
                setAlignment(formatType || 'left');
            });
        });
    }, [editor]);

    const btn = (title, active, onClick, content) => (
        <button
            key={title}
            type="button"
            title={title}
            className={'lexical-toolbar-btn' + (active ? ' active' : '')}
            onMouseDown={e => { e.preventDefault(); onClick(); }}
        >
            {content}
        </button>
    );

    const sep = key => <div key={key} className="lexical-toolbar-sep" />;

    const toggleList = type => {
        if (listType === type) {
            editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
        } else if (type === 'bullet') {
            editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
        } else {
            editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
        }
    };

    return (
        <div className="lexical-toolbar">
            {btn('Bold',        activeFormats.bold,          () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold'),          <b>B</b>)}
            {btn('Italic',      activeFormats.italic,        () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic'),        <i>I</i>)}
            {sep('sep1')}
            {btn('Bullet list',   listType === 'bullet',   () => toggleList('bullet'),   '•≡')}
            {sep('sep3')}
            {btn('Undo', false, () => editor.dispatchCommand(UNDO_COMMAND, undefined), '↩')}
            {btn('Redo', false, () => editor.dispatchCommand(REDO_COMMAND, undefined), '↪')}
        </div>
    );
}

// ── Read-only toggle ─────────────────────────────────────────────────────────

function ReadOnlyPlugin({ readOnly }) {
    const [editor] = useLexicalComposerContext();

    useEffect(() => {
        editor.setEditable(!readOnly);
    }, [editor, readOnly]);

    return null;
}

// ── ImperativeHandle bridge ───────────────────────────────────────────────────

function EditorBridge({ forwardedRef, onFirstChange, isLoadingRef }) {
    const [editor] = useLexicalComposerContext();
    const dirtyRef = useRef(false);

    useImperativeHandle(forwardedRef, () => ({
        getData() {
            return editor.getEditorState().read(() => $generateHtmlFromNodes(editor, null));
        },
        getValue() {
            return editor.getEditorState().read(() => $generateHtmlFromNodes(editor, null));
        },
        setData(html) {
            isLoadingRef.current = true;
            editor.update(() => {
                const parser = new DOMParser();
                const dom = parser.parseFromString(html || '', 'text/html');
                const nodes = $generateNodesFromDOM(editor, dom);
                const root = $getRoot();
                root.clear();
                root.append(...nodes);
            }, {
                onUpdate: () => { isLoadingRef.current = false; }
            });
        },
        isDirty() {
            return dirtyRef.current;
        },
        setDirty(value) {
            dirtyRef.current = value;
        },
        focus() {
            editor.focus();
        },
    }), [editor]);

    const handleChange = useCallback((editorState) => {
        if (isLoadingRef.current) return;
        if (!dirtyRef.current) {
            dirtyRef.current = true;
            if (onFirstChange) onFirstChange();
        }
    }, [onFirstChange, isLoadingRef]);

    return <OnChangePlugin onChange={handleChange} ignoreSelectionChange={true} />;
}

// ── Main export ───────────────────────────────────────────────────────────────

const theme = {
    text: {
        bold: 'lexical-text-bold',
        italic: 'lexical-text-italic',
        underline: 'lexical-text-underline',
        strikethrough: 'lexical-text-strikethrough',
    },
    list: {
        ul: 'lexical-list-ul',
        ol: 'lexical-list-ol',
    },
};

const EDITOR_NODES = [HeadingNode, QuoteNode, ListNode, ListItemNode, AutoLinkNode, LinkNode];

const LexicalRichTextEditor = React.forwardRef((props, ref) => {
    const isLoadingRef = useRef(true);
    const height = props.config?.height ?? 100;
    const onFirstChange = props.events?.change;
    const readOnly = props.readOnly ?? false;

    const initialConfig = {
        namespace: 'LexicalRichTextEditor',
        theme,
        nodes: EDITOR_NODES,
        editable: !readOnly,
        onError(error) { console.error(error); },
    };

    return (
        <LexicalComposer initialConfig={initialConfig}>
            <div className="lexical-editor-wrapper">
                {!readOnly && <ToolbarPlugin />}
                <RichTextPlugin
                    contentEditable={
                        <ContentEditable
                            className="lexical-editable"
                            style={{ minHeight: height + 'px' }}
                        />
                    }
                    placeholder={null}
                    ErrorBoundary={LexicalErrorBoundary}
                />
                <HistoryPlugin />
                <ListPlugin />
                <LinkPlugin />
                <ReadOnlyPlugin readOnly={readOnly} />
                <EditorBridge
                    forwardedRef={ref}
                    onFirstChange={onFirstChange}
                    isLoadingRef={isLoadingRef}
                />
                <InitialValuePlugin html={props.value} isLoadingRef={isLoadingRef} />
            </div>
        </LexicalComposer>
    );
});

// Loads the initial HTML value once the editor is mounted
function InitialValuePlugin({ html, isLoadingRef }) {
    const [editor] = useLexicalComposerContext();
    const loaded = useRef(false);

    useEffect(() => {
        if (loaded.current) return;
        loaded.current = true;

        isLoadingRef.current = true;
        editor.update(() => {
            const parser = new DOMParser();
            const dom = parser.parseFromString(html || '', 'text/html');
            const nodes = $generateNodesFromDOM(editor, dom);
            const root = $getRoot();
            root.clear();
            if (nodes.length > 0) {
                root.append(...nodes);
            }
        }, {
            onUpdate: () => { isLoadingRef.current = false; }
        });
    }, [editor]);

    return null;
}

export default LexicalRichTextEditor;